
import React, { useEffect, useState } from 'react';
import { useParams, useNavigate, useLocation } from 'react-router-dom';
import { Header } from '../../components/feature/Header';
import { Footer } from '../home/components/Footer';
import { fetchPropertyById, PublicProperty } from '../../../pro-admin/lib/api';

interface Review {
  id: string;
  name: string;
  rating: number;
  comment: string;
  date: string;
  avatar: string;
}

interface PropertyDetails {
  id: string;
  title: string;
  location: string;
  price: string;
  images: string[];
  rating: number;
  reviews: number;
  description: string;
  features: string[];
  specifications: Record<string, string>;
  amenities: string[];
  category: string;
  reviewsList: Review[];
}

const PropertyDetailsPage: React.FC = () => {
  const { category, id } = useParams();
  const navigate = useNavigate();
  const location = useLocation() as any;
  const passedProperty = (location?.state?.property as PublicProperty | undefined) || null;
  const [currentImageIndex, setCurrentImageIndex] = useState(0);
  const [showReviewForm, setShowReviewForm] = useState(false);
  const [newReview, setNewReview] = useState({
    name: '',
    rating: 5,
    comment: ''
  });

  const [property, setProperty] = useState<PublicProperty | null>(passedProperty);
  const [loading, setLoading] = useState(!passedProperty);
  const [error, setError] = useState<string | null>(null);
  const [reviewsList, setReviewsList] = useState<Review[]>([]);
  const [showBookingModal, setShowBookingModal] = useState(false);
  const [bookingForm, setBookingForm] = useState({
    fullName: '',
    phone: '',
    email: '',
    durationDays: 1,
    startDate: '',
    notes: '',
    submitting: false,
  });

  useEffect(() => {
    const fetchPropertyAndReviews = async () => {
      if (!id) {
        setError('Property ID is missing');
        setLoading(false);
        return;
      }
      try {
        setLoading(prev => prev || false);
        const item = await fetchPropertyById(id);
        if (item) setProperty(item);
        // Attempt reviews fetch without failing page
        try {
          const reviewsResponse = await fetch(`/api/reviews?propertyId=${id}`);
          if (reviewsResponse.ok) {
            const reviewsData = await reviewsResponse.json();
            if (Array.isArray(reviewsData?.data)) {
              setReviewsList(reviewsData.data);
            }
          }
        } catch (_) {
          // ignore reviews errors
        }
      } catch (err: any) {
        setError(err.message);
      } finally {
        setLoading(false);
      }
    };

    fetchPropertyAndReviews();
  }, [id]);

  const handleReviewSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!id) {
      alert('Cannot submit review: Property ID is missing.');
      return;
    }
    try {
      const response = await fetch('/api/reviews', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          propertyId: id,
          name: newReview.name,
          rating: newReview.rating,
          comment: newReview.comment,
        }),
      });

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const result = await response.json();
      if (result.data) {
        setReviewsList(prevReviews => [result.data, ...prevReviews]);
        setNewReview({ name: '', rating: 5, comment: '' });
        setShowReviewForm(false);
      }
    } catch (err: any) {
      alert(`Failed to submit review: ${err.message}`);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="text-center">
          <h1 className="text-2xl font-bold text-gray-900 mb-4">Loading Property Details...</h1>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="text-center">
          <h1 className="text-2xl font-bold text-red-600 mb-4">Error: {error}</h1>
          <button
            onClick={() => navigate('/')}
            className="bg-blue-600 text-white px-6 py-3 rounded-lg hover:bg-blue-700 transition-colors"
          >
            Back to Home
          </button>
        </div>
      </div>
    );
  }

  if (!property) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="text-center">
          <h1 className="text-2xl font-bold text-gray-900 mb-4">Property Not Found</h1>
          <button
            onClick={() => navigate('/')}
            className="bg-blue-600 text-white px-6 py-3 rounded-lg hover:bg-blue-700 transition-colors"
          >
            Back to Home
          </button>
        </div>
      </div>
    );
  }
  // Define safe fallbacks after property is confirmed
  const images: string[] = (Array.isArray(property.images) && property.images.length > 0)
    ? property.images
    : (property.image_url ? [property.image_url] : []);
  const priceText = (typeof property.display_price === 'number')
    ? `${property.display_price.toLocaleString()}${property.display_price_label && property.display_price_label !== 'full' ? ` / ${property.display_price_label}` : ''}`
    : '';
  // Live booking price calculation
  const unitPrice = typeof property.display_price === 'number' ? property.display_price : 0;
  const priceLabel = property.display_price_label || 'night';
  const isSale = priceLabel === 'full';
  const totalBookingPrice = isSale ? unitPrice : unitPrice * Math.max(1, bookingForm.durationDays || 1);
  const priceTypeText = isSale ? 'For Sale' : (priceLabel === 'day' ? 'Per Day' : 'Per Night');
  const computedFeatures: string[] = [
    property.bedrooms ? `${property.bedrooms} bedrooms` : undefined,
    property.bathrooms ? `${property.bathrooms} bathrooms` : undefined,
    property.area_sqft ? `${property.area_sqft} sqft` : undefined,
    property.category ? `${property.category}` : undefined,
  ].filter(Boolean) as string[];
  const specifications: Record<string, string> = {
    ...(property.status ? { Status: property.status } : {}),
    ...(property.max_guests ? { 'Max Guests': String(property.max_guests) } : {}),
    ...(property.check_in_time ? { 'Check-in': property.check_in_time } : {}),
    ...(property.check_out_time ? { 'Check-out': property.check_out_time } : {}),
    ...(property.cancellation_policy ? { 'Cancellation': property.cancellation_policy } : {}),
    ...(property.whatsapp_number ? { 'WhatsApp': property.whatsapp_number } : {}),
  };
  const whatsappRaw = (property.whatsapp_number || '').trim();
  const whatsappDigits = whatsappRaw.replace(/\D/g, '');
  const whatsappLink = whatsappDigits ? `https://wa.me/${whatsappDigits}` : '';
  // Helper to render video either via iframe for YouTube or video tag for direct URLs
  const fallbackRatings = [3, 4, 4.5];
  const pickFallbackRating = (id?: string) => {
    const base = (id || '').split('').reduce((acc, c) => acc + c.charCodeAt(0), 0);
    return fallbackRatings[base % fallbackRatings.length];
  };
  const displayRating = (typeof property.rating === 'number' && property.rating > 0)
    ? property.rating
    : pickFallbackRating(property.id);
   const renderVideo = () => {
    const url = property.video_url;
    if (!url) return null;
    const lower = url.toLowerCase();
    const isYouTube = lower.includes('youtube.com') || lower.includes('youtu.be');
    if (isYouTube) {
      // Extract YouTube ID crudely
      let videoId = '';
      const match = url.match(/[?&]v=([^&]+)/);
      if (match) videoId = match[1];
      const shortMatch = url.match(/youtu\.be\/([A-Za-z0-9_-]+)/);
      if (!videoId && shortMatch) videoId = shortMatch[1];
      const embedSrc = videoId ? `https://www.youtube.com/embed/${videoId}` : url;
      return (
        <div className="aspect-video rounded-xl overflow-hidden bg-black">
          <iframe src={embedSrc} title="Property Video" allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture" allowFullScreen className="w-full h-full" />
        </div>
      );
    }
    return (
      <div className="aspect-video rounded-xl overflow-hidden bg-black">
        <video src={url} controls className="w-full h-full" />
      </div>
    );
  };
  return (
    <div className="min-h-screen bg-white">
      <Header />
      
      <main className="pt-14 sm:pt-16 md:pt-20">

        {/* Image Gallery with Dark Effect */}
        <div className="relative">
          <div className="aspect-video sm:aspect-[16/10] md:aspect-[16/9] lg:aspect-[21/9] bg-gray-900 overflow-hidden">
            <div className="relative w-full h-full">
              <img
                src={images[currentImageIndex]}
                alt={property.title}
                className="w-full h-full object-cover object-top"
              />
              {/* Dark overlay for cool effect */}
              <div className="absolute inset-0 bg-gradient-to-t from-black/60 via-black/20 to-transparent"></div>
              {/* Property info overlay */}
              <div className="absolute bottom-4 sm:bottom-6 md:bottom-8 left-4 sm:left-6 md:left-8 text-white z-10">
                <h1 className="text-2xl sm:text-3xl md:text-4xl lg:text-5xl font-bold mb-2" style={{ fontFamily: 'Playfair Display, serif' }}>
                  {property.title}
                </h1>
                <div className="flex items-center text-lg sm:text-xl mb-2">
                  <i className="ri-map-pin-line mr-2 text-blue-400"></i>
                  <span>{property.location}</span>
                </div>
                <p className="text-2xl sm:text-3xl md:text-4xl font-bold text-blue-400">
                  {priceText}
                </p>
                {isSale && (
                  <div className="mt-2">
                    <span className="inline-block bg-red-600 text-white text-lg sm:text-xl md:text-2xl font-extrabold px-3 py-1 rounded-lg uppercase tracking-wide">
                      FOR SALE
                    </span>
                  </div>
                )}
              </div>
            </div>
            {/* Image Navigation */}
            {images.length > 1 && (
              <>
                <button
                  onClick={() => setCurrentImageIndex(prev => prev === 0 ? images.length - 1 : prev - 1)}
                  className="absolute left-2 sm:left-4 top-1/2 transform -translate-y-1/2 bg-black/50 text-white p-2 sm:p-3 rounded-full hover:bg-black/70 transition-colors backdrop-blur-sm"
                >
                  <i className="ri-arrow-left-line text-lg sm:text-xl"></i>
                </button>
                <button
                  onClick={() => setCurrentImageIndex(prev => prev === images.length - 1 ? 0 : prev + 1)}
                  className="absolute right-2 sm:right-4 top-1/2 transform -translate-y-1/2 bg-black/50 text-white p-2 sm:p-3 rounded-full hover:bg-black/70 transition-colors backdrop-blur-sm"
                >
                  <i className="ri-arrow-right-line text-lg sm:text-xl"></i>
                </button>
              </>
            )}
            {/* Image Indicators */}
            {images.length > 1 && (
              <div className="absolute bottom-3 sm:bottom-4 right-4 sm:right-6 flex space-x-1.5 sm:space-x-2">
                {images.map((_, index) => (
                  <button
                    key={index}
                    onClick={() => setCurrentImageIndex(index)}
                    className={`w-2 h-2 sm:w-3 sm:h-3 rounded-full transition-all ${
                      index === currentImageIndex ? 'bg-white' : 'bg-white/50'
                    }`}
                  />
                ))}
              </div>
            )}
            {/* Back Button */}
            <button
              onClick={() => navigate('/')}
              className="absolute top-3 sm:top-4 left-3 sm:left-4 bg-black/50 text-white p-2 sm:p-3 rounded-full hover:bg-black/70 transition-colors backdrop-blur-sm"
            >
              <i className="ri-arrow-left-line text-lg sm:text-xl"></i>
            </button>
          </div>
        </div>

        {/* Property Details */}
        <div className="w-full px-3 sm:px-4 md:px-6 lg:px-8 xl:px-12 py-6 sm:py-8 md:py-12">
          <div className="max-w-7xl mx-auto">
            <div className="grid grid-cols-1 lg:grid-cols-3 gap-6 sm:gap-8 md:gap-12">
              {/* Main Content */}
              <div className="lg:col-span-2 space-y-6 sm:space-y-8">
                {/* Rating */}
                <div className="flex items-center space-x-4 p-4 bg-gray-50 rounded-xl">
                  <div className="flex items-center space-x-2">
                    <i className="ri-star-fill text-yellow-400 text-2xl"></i>
                    <span className="text-2xl font-bold">{displayRating}</span>
                  </div>
                  <div className="text-gray-600">
                    <span className="font-semibold">{reviewsList.length} reviews</span>
                  </div>
                </div>

                {/* Description */}
                 <div>
                   <h2 className="text-xl sm:text-2xl md:text-3xl font-bold text-gray-900 mb-3 sm:mb-4">Description</h2>
                   <p className="text-sm sm:text-base md:text-lg text-gray-600 leading-relaxed">
                     {property.description || 'No description available.'}
                   </p>
                   {/* Video Section (backend-driven) */}
                   {property.video_url && (
                     <div className="mt-4 sm:mt-6">
                       <h3 className="text-lg sm:text-xl font-semibold text-gray-900 mb-2 sm:mb-3">Property Video</h3>
                       {renderVideo()}
                     </div>
                   )}
                 </div>

                {/* Features */}
                <div>
                  <h2 className="text-xl sm:text-2xl md:text-3xl font-bold text-gray-900 mb-3 sm:mb-4">Features</h2>
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-2 sm:gap-3">
                    {computedFeatures.map((feature, index) => (
                      <div key={index} className="flex items-center text-sm sm:text-base text-gray-700">
                        <i className="ri-check-line text-green-500 mr-2 sm:mr-3 text-lg"></i>
                        <span>{feature}</span>
                      </div>
                    ))}
                  </div>
                </div>

                {/* Specifications */}
                <div>
                  <h2 className="text-xl sm:text-2xl md:text-3xl font-bold text-gray-900 mb-3 sm:mb-4">Specifications</h2>
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 sm:gap-4">
                    {Object.entries(specifications).map(([key, value]) => (
                      <div key={key} className="flex justify-between items-center py-2 sm:py-3 border-b border-gray-200">
                        <span className="text-sm sm:text-base font-medium text-gray-700">{key}</span>
                        {key === 'WhatsApp' ? (
                          <div className="flex items-center">
                            <button
                              onClick={() => setShowBookingModal(true)}
                              className="bg-green-600 hover:bg-green-700 text-white px-3 py-1.5 rounded-md text-sm sm:text-base"
                            >
                              Send Message
                            </button>
                          </div>
                        ) : (
                          <span className="text-sm sm:text-base text-gray-900">{value}</span>
                        )}
                      </div>
                    ))}
                  </div>
                </div>

                {/* Amenities */}
                <div>
                  <h2 className="text-xl sm:text-2xl md:text-3xl font-bold text-gray-900 mb-3 sm:mb-4">Amenities</h2>
                  <div className="grid grid-cols-2 sm:grid-cols-3 gap-2 sm:gap-3">
                    {Array.isArray(property.amenities) && property.amenities.map((amenity, index) => (
                      <div key={index} className="flex items-center text-sm sm:text-base text-gray-700">
                        <i className="ri-check-line text-blue-600 mr-2 text-lg"></i>
                        <span>{amenity}</span>
                      </div>
                    ))}
                  </div>
                </div>

                {/* Reviews Section */}
                <div id="reviews-section">
                  <div className="flex items-center justify-between mb-6">
                    <h2 className="text-xl sm:text-2xl md:text-3xl font-bold text-gray-900">Reviews</h2>
                    <button
                      onClick={() => setShowReviewForm(!showReviewForm)}
                      className="bg-blue-600 text-white px-4 py-2 rounded-lg hover:bg-blue-700 transition-colors text-sm font-medium"
                    >
                      <i className="ri-add-line mr-2"></i>
                      Write Review
                    </button>
                  </div>

                  {/* Review Form */}
                  {showReviewForm && (
                    <div className="bg-gray-50 rounded-xl p-6 mb-6">
                      <h3 className="text-lg font-semibold mb-4">Write a Review</h3>
                      <form onSubmit={handleReviewSubmit} className="space-y-4">
                        <div>
                          <label className="block text-sm font-medium text-gray-700 mb-2">Name</label>
                          <input
                            type="text"
                            value={newReview.name}
                            onChange={(e) => setNewReview({...newReview, name: e.target.value})}
                            className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                            required
                          />
                        </div>
                        <div>
                          <label className="block text-sm font-medium text-gray-700 mb-2">Rating</label>
                          <div className="flex space-x-1">
                            {[1, 2, 3, 4, 5].map((star) => (
                              <button
                                key={star}
                                type="button"
                                onClick={() => setNewReview({...newReview, rating: star})}
                                className={`text-2xl ${star <= newReview.rating ? 'text-yellow-400' : 'text-gray-300'}`}
                              >
                                <i className="ri-star-fill"></i>
                              </button>
                            ))}
                          </div>
                        </div>
                        <div>
                          <label className="block text-sm font-medium text-gray-700 mb-2">Comment</label>
                          <textarea
                            value={newReview.comment}
                            onChange={(e) => setNewReview({...newReview, comment: e.target.value})}
                            rows={4}
                            maxLength={500}
                            className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                            placeholder="Share your experience..."
                            required
                          />
                          <p className="text-xs text-gray-500 mt-1">{newReview.comment.length}/500 characters</p>
                        </div>
                        <div className="flex space-x-3">
                          <button
                            type="submit"
                            className="bg-blue-600 text-white px-6 py-2 rounded-lg hover:bg-blue-700 transition-colors"
                          >
                            Submit Review
                          </button>
                          <button
                            type="button"
                            onClick={() => setShowReviewForm(false)}
                            className="bg-gray-300 text-gray-700 px-6 py-2 rounded-lg hover:bg-gray-400 transition-colors"
                          >
                            Cancel
                          </button>
                        </div>
                      </form>
                    </div>
                  )}

                  {/* Reviews List */}
                  <div className="space-y-4">
                    {reviewsList.map((review) => (
                      <div key={review.id} className="bg-white border border-gray-200 rounded-xl p-6">
                        <div className="flex items-start space-x-4">
                          <div className="w-12 h-12 bg-blue-600 rounded-full flex items-center justify-center text-white font-semibold">
                            {review.name.charAt(0).toUpperCase()}
                          </div>
                          <div className="flex-1">
                            <div className="flex items-center justify-between mb-2">
                              <h4 className="font-semibold text-gray-900">{review.name}</h4>
                              <span className="text-sm text-gray-500">{new Date(review.date).toLocaleDateString()}</span>
                            </div>
                            <div className="flex items-center mb-2">
                              {[1, 2, 3, 4, 5].map((star) => (
                                <i
                                  key={star}
                                  className={`ri-star-fill text-sm ${star <= review.rating ? 'text-yellow-400' : 'text-gray-300'}`}
                                ></i>
                              ))}
                            </div>
                            <p className="text-gray-600">{review.comment}</p>
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              </div>

              {/* Sidebar */}
              <div className="lg:col-span-1">
                <div className="bg-gray-50 rounded-xl sm:rounded-2xl p-4 sm:p-6 md:p-8 sticky top-24">
                  <h3 className="text-xl sm:text-2xl font-bold text-gray-900 mb-4 sm:mb-6">Contact Agent</h3>
                  {/* Security Notice */}
                  <div className="bg-red-50 border-2 border-red-300 text-red-800 rounded-lg p-4 mb-5">
                    <div className="flex items-start">
                      <i className="ri-alert-fill mr-2 mt-0.5 text-lg"></i>
                      <p className="text-sm sm:text-base font-bold">Keep a screenshot and record of your transaction before transferring money.</p>
                    </div>
                  </div>
                   
                   <div className="space-y-3 sm:space-y-4 mb-6 sm:mb-8">
                     <button
                       onClick={() => setShowBookingModal(true)}
                       className="w-full bg-green-600 hover:bg-green-700 text-white py-3 sm:py-4 rounded-lg sm:rounded-xl text-sm sm:text-base font-semibold transition-colors whitespace-nowrap"
                     >
                       <i className="ri-whatsapp-line mr-2"></i>
                       Send Message
                     </button>
                     <button
                       onClick={() => setShowBookingModal(true)}
                       className="w-full border-2 border-blue-600 text-blue-600 hover:bg-blue-600 hover:text-white py-3 sm:py-4 rounded-lg sm:rounded-xl text-sm sm:text-base font-semibold transition-colors whitespace-nowrap"
                     >
                       <i className="ri-calendar-line mr-2"></i>
                       Schedule Tour
                     </button>
                   </div>
 
                   {/* Reviews quick access - always visible in sidebar */}
                   <div className="bg-white rounded-lg border border-gray-200 p-4 mb-6">
                     <div className="flex items-center justify-between">
                       <div className="flex items-center space-x-2">
                         <i className="ri-star-fill text-yellow-400"></i>
                         <span className="text-sm font-semibold text-gray-900">{displayRating}</span>
                         <span className="text-sm text-gray-600">({reviewsList.length} reviews)</span>
                       </div>
                       <a href="#reviews-section" className="text-blue-600 hover:text-blue-700 text-sm font-medium">See reviews</a>
                     </div>
                   </div>
 
                   {/* Google Map */}
                   <div className="mb-4 sm:mb-6">
                     <h4 className="text-lg sm:text-xl font-semibold text-gray-900 mb-3 sm:mb-4">Location</h4>
                     <div className="aspect-video rounded-lg sm:rounded-xl overflow-hidden">
                       {property.latitude && property.longitude ? (
                         <iframe
                           src={`https://www.google.com/maps?q=${property.latitude},${property.longitude}&output=embed`}
                           width="100%"
                           height="100%"
                           style={{ border: 0 }}
                           allowFullScreen
                           loading="lazy"
                           referrerPolicy="no-referrer-when-downgrade"
                           title="Property Location"
                         ></iframe>
                       ) : property.location ? (
                         <iframe
                           src={`https://www.google.com/maps?q=${encodeURIComponent(property.location)}&output=embed`}
                           width="100%"
                           height="100%"
                           style={{ border: 0 }}
                           allowFullScreen
                           loading="lazy"
                           referrerPolicy="no-referrer-when-downgrade"
                           title="Property Location"
                         ></iframe>
                       ) : null}
                     </div>
                     {(property.location || (property.latitude && property.longitude)) && (
                       <a
                         href={property.latitude && property.longitude
                           ? `https://www.google.com/maps/search/?api=1&query=${property.latitude},${property.longitude}`
                           : `https://www.google.com/maps/search/${encodeURIComponent(property.location || '')}`}
                         target="_blank"
                         rel="noopener noreferrer"
                         className="inline-flex items-center text-blue-600 hover:text-blue-700 text-sm sm:text-base mt-2 sm:mt-3"
                       >
                         <i className="ri-external-link-line mr-1 sm:mr-2"></i>
                         Open in Google Maps
                       </a>
                     )}
                   </div>
 
                   {/* Nearby Places (backend-driven) */}
                   {Array.isArray((property as any).nearby) && (property as any).nearby.length > 0 && (
                     <div>
                       <h4 className="text-lg sm:text-xl font-semibold text-gray-900 mb-3 sm:mb-4">Nearby</h4>
                       <div className="space-y-2 sm:space-y-3">
                         {((property as any).nearby as Array<{ name: string; distance?: string }>).map((place, idx) => (
                           <div key={idx} className="flex justify-between items-center text-sm sm:text-base">
                             <span className="text-gray-600">{place.name}</span>
                             {place.distance && <span className="text-gray-900 font-medium">{place.distance}</span>}
                           </div>
                         ))}
                       </div>
                     </div>
                   )}
                </div>
              </div>
            </div>
          </div>
        </div>
      </main>

      {whatsappLink && (
         <button
           onClick={() => setShowBookingModal(true)}
           className="fixed bottom-4 left-4 sm:bottom-6 sm:left-6 z-50 bg-green-600 hover:bg-green-700 text-white px-4 sm:px-5 py-3 sm:py-3.5 rounded-full shadow-lg focus:outline-none"
         >
           <i className="ri-whatsapp-line mr-2"></i>
           Send Message
         </button>
       )}

      {/* Booking Modal */}
      {showBookingModal && (
        <div className="fixed inset-0 z-[60] flex items-center justify-center bg-black/50">
          <div className="bg-white w-full max-w-md rounded-2xl p-6 shadow-xl">
            <div className="flex items-center justify-between mb-4">
              <h2 className="text-lg sm:text-xl font-bold">Booking Details</h2>
              <button onClick={() => setShowBookingModal(false)} className="p-2 hover:bg-gray-100 rounded-lg">
                <i className="ri-close-line text-xl"></i>
              </button>
            </div>
            <form
              onSubmit={async (e) => {
                e.preventDefault();
                try {
                  setBookingForm(prev => ({ ...prev, submitting: true }));

                  const message =
                    `Alert of booking from Lucahomes\n\n` +
                    `Property: ${property.title || property.display_name || ''}\n` +
                    (property.location ? `Location: ${property.location}\n` : '') +
                    (priceText ? `Price: ${priceText}\n` : '') +
                    `- Pricing Type: ${priceTypeText}\n` +
                    `\nGuest Details\n` +
                    `- Name: ${bookingForm.fullName}\n` +
                    (bookingForm.phone ? `- Phone: ${bookingForm.phone}\n` : '') +
                    (bookingForm.email ? `- Email: ${bookingForm.email}\n` : '') +
                    (bookingForm.startDate ? `- Start Date: ${bookingForm.startDate}\n` : '') +
                    `- Duration: ${priceLabel === 'night' ? bookingForm.durationDays + ' night(s)' : priceLabel === 'day' ? bookingForm.durationDays + ' day(s)' : 'N/A'}\n` +
                    `- Total Price: ${totalBookingPrice.toLocaleString()}\n` +
                    (bookingForm.notes ? `\nNotes\n${bookingForm.notes}\n` : '') +
                    `\nNote: A 20% commission is payable to Lucahomes for all bookings.\n` +
                    `\nPlease respond to confirm availability.`;

                  // Best-effort persist to admin (Mongo) without blocking WhatsApp/email
                  let saveFailed = false;
                  try {
                    const payload = {
                      propertyId: property.id,
                      propertyTitle: property.title || property.display_name || '',
                      propertyLocation: property.location || '',
                      priceText,
                      fullName: bookingForm.fullName,
                      phone: bookingForm.phone,
                      email: bookingForm.email,
                      durationDays: bookingForm.durationDays,
                      startDate: bookingForm.startDate,
                      notes: bookingForm.notes,
                    };
                    const attempts = 3;
                    let saved = false;
                    for (let i = 0; i < attempts && !saved; i++) {
                      const res = await fetch('/api/bookings', {
                        method: 'POST',
                        headers: { 'Content-Type': 'application/json' },
                        body: JSON.stringify(payload),
                      });
                      if (res.ok) {
                        saved = true;
                      } else {
                        const text = await res.text().catch(() => '');
                        console.error('Failed to save booking:', res.status, text);
                        await new Promise(r => setTimeout(r, 400));
                      }
                    }
                    saveFailed = !saved;
                  } catch (err) {
                    console.error('Error saving booking:', err);
                    saveFailed = true;
                  }

                  // Open WhatsApp to the property owner if available
                  if (whatsappLink) {
                    const encoded = encodeURIComponent(message);
                    const url = `${whatsappLink}?text=${encoded}`;
                    window.open(url, '_blank');
                  }

                  setShowBookingModal(false);
                  setBookingForm({ fullName: '', phone: '', email: '', durationDays: 1, startDate: '', notes: '', submitting: false });

                  if (saveFailed) {
                    setTimeout(() => {
                      alert('Your booking message was opened in WhatsApp. Saving to admin failed.');
                    }, 0);
                  }
                } catch (err: any) {
                  alert(err.message || 'Failed to submit booking request.');
                } finally {
                  setBookingForm(prev => ({ ...prev, submitting: false }));
                }
              }}
              className="space-y-3"
            >
              <div>
                <label className="block text-sm font-medium text-gray-700">Full Name</label>
                <input
                  type="text"
                  className="mt-1 w-full border rounded-lg px-3 py-2"
                  value={bookingForm.fullName}
                  onChange={e => setBookingForm(prev => ({ ...prev, fullName: e.target.value }))}
                  required
                />
              </div>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                <div>
                  <label className="block text-sm font-medium text-gray-700">Phone</label>
                  <input
                    type="tel"
                    className="mt-1 w-full border rounded-lg px-3 py-2"
                    value={bookingForm.phone}
                    onChange={e => setBookingForm(prev => ({ ...prev, phone: e.target.value }))}
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700">Email</label>
                  <input
                    type="email"
                    className="mt-1 w-full border rounded-lg px-3 py-2"
                    value={bookingForm.email}
                    onChange={e => setBookingForm(prev => ({ ...prev, email: e.target.value }))}
                  />
                </div>
              </div>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                <div>
                  <label className="block text-sm font-medium text-gray-700">Duration ({priceLabel === 'night' ? 'nights' : 'days'})</label>
                  <input
                    type="number"
                    min={1}
                    className="mt-1 w-full border rounded-lg px-3 py-2"
                    value={bookingForm.durationDays}
                    onChange={e => setBookingForm(prev => ({ ...prev, durationDays: Number(e.target.value) }))}
                    required
                    disabled={priceLabel === 'full'}
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700">Start Date</label>
                  <input
                    type="date"
                    className="mt-1 w-full border rounded-lg px-3 py-2"
                    value={bookingForm.startDate}
                    onChange={e => setBookingForm(prev => ({ ...prev, startDate: e.target.value }))}
                  />
                </div>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700">Notes</label>
                <textarea
                  className="mt-1 w-full border rounded-lg px-3 py-2"
                  rows={3}
                  value={bookingForm.notes}
                  onChange={e => setBookingForm(prev => ({ ...prev, notes: e.target.value }))}
                />
                {/* Live total price display */}
                <div className="mt-2 text-sm text-gray-700">
                  <span className="font-medium">Total Price:</span>
                  <span className="ml-1 text-blue-600 font-semibold">{totalBookingPrice.toLocaleString()}</span>
                  {priceLabel !== 'full' && (
                    <span className="ml-1 text-gray-500">({bookingForm.durationDays} {priceLabel === 'night' ? 'night(s)' : 'day(s)'})</span>
                  )}
                  {priceLabel === 'full' && (
                    <span className="ml-1 text-gray-500">(For Sale)</span>
                  )}
                </div>
              </div>
              <button
                type="submit"
                disabled={bookingForm.submitting}
                className="w-full bg-green-600 text-white py-2.5 rounded-lg hover:bg-green-700 transition-colors disabled:opacity-60"
              >
                {bookingForm.submitting ? 'Opening WhatsApp...' : 'Book'}
              </button>
            </form>
          </div>
        </div>
      )}
 
      {/* Nearby Places (backend-driven) */}
      {Array.isArray((property as any).nearby) && (property as any).nearby.length > 0 && (
        <div>
          <h4 className="text-lg sm:text-xl font-semibold text-gray-900 mb-3 sm:mb-4">Nearby</h4>
          <div className="space-y-2 sm:space-y-3">
            {((property as any).nearby as Array<{ name: string; distance?: string }>).map((place, idx) => (
              <div key={idx} className="flex justify-between items-center text-sm sm:text-base">
                <span className="text-gray-600">{place.name}</span>
                {place.distance && <span className="text-gray-900 font-medium">{place.distance}</span>}
              </div>
            ))}
          </div>
        </div>
      )}
      <Footer />
    </div>
  );
};

export default PropertyDetailsPage;
